import PropTypes from "prop-types";

export const STORAGE_KEY = "isCourtedOpen";
export const PANEL_ID = "courted-panel-content";

// Semantic tokens map to Bootstrap 4 badge classes. Fields and the legend
// reference the token; the class is computed via `badgeClassForToken`.
const BADGE_CLASS_BY_TOKEN = Object.freeze({
  success: "badge badge-success",
  primary: "badge badge-primary",
  secondary: "badge badge-secondary",
  warning: "badge badge-warning",
});

export const badgeClassForToken = (token) => BADGE_CLASS_BY_TOKEN[token];

// Each Boolean field carries a `badgeToken`. `badgeTokenNo` is only set for
// fields whose color flips when value is false (e.g. trouble_at_office).
export const BOOLEAN_FIELDS = Object.freeze({
  at_gte_one_transaction: {
    label: "Agent Type: Producing",
    badgeToken: "success",
    tooltip: "Closed at least one transaction in the last twelve months.",
  },
  at_manager_managing_broker: {
    label: "Manager / Managing Broker",
    badgeToken: "primary",
    tooltip: "Holds a manager or managing broker role at the current office.",
  },
  at_mostly_buy: {
    label: "Agent Type: Mostly Buy-side",
    badgeToken: "secondary",
    tooltip: "Most transactions in the last twelve months were buy-side.",
  },
  at_mostly_list: {
    label: "Agent Type: Mostly List-side",
    badgeToken: "secondary",
    tooltip: "Most transactions in the last twelve months were list-side.",
  },
  at_ready_to_move: {
    label: "Agent Type: Ready to Move",
    badgeToken: "success",
    tooltip:
      "Behavioral signals consistent with being open to switching brokerages.",
  },
  at_rising_star: {
    label: "Rising Star Agent",
    badgeToken: "success",
    tooltip:
      "Has shown consistent sales volume growth over the past year, marking them as an upward-trending performer.",
  },
  at_team_leader: {
    label: "Likely Team Leader",
    badgeToken: "primary",
    tooltip:
      "Likely leads a real estate team — typically responsible for the team's production and other team members' output.",
  },
  at_team_member: {
    label: "Likely Team Member",
    badgeToken: "primary",
    tooltip:
      "Likely part of a real estate team as a member, not the team lead.",
  },
  at_trouble_at_office: {
    label: "Trouble at Current Office",
    badgeToken: "warning",
    badgeTokenNo: "success",
    tooltip:
      "Indicates potential issues or instability at their current office.",
  },
});

// Human-readable legend metadata keyed by semantic token. The legend tooltip
// is derived from BOOLEAN_FIELDS at render time so only tokens actually used
// in the panel appear in the legend.
export const BADGE_DESCRIPTIONS = Object.freeze({
  success: {
    label: "Green",
    description: "Positive recruiting signal",
  },
  primary: {
    label: "Blue",
    description: "Team / role flag",
  },
  secondary: {
    label: "Grey",
    description: "Neutral style descriptor",
  },
  warning: {
    label: "Amber",
    description: "Warning signal",
  },
});

// Renderer dispatch keys. The renderers themselves live in Section.jsx
// because they emit JSX; this file holds the constants only.
export const KIND = Object.freeze({
  text: "text",
  currency: "currency",
  decimal: "decimal",
  int: "int",
  tenure: "tenure",
  date: "date",
  bool: "bool",
});

// Boolean fields project BOOLEAN_FIELDS into the SECTION_DEFS field shape so
// the Classification section stays a flat list with no per-field literals.
const BOOLEAN_SECTION_FIELDS = Object.entries(BOOLEAN_FIELDS).map(
  ([key, field]) => ({
    key,
    label: field.label,
    tooltip: field.tooltip,
    kind: KIND.bool,
    booleanField: field,
  }),
);

// Single source of truth: every panel section + its fields lives here. Add a
// section by extending this array — no new component needed.
export const SECTION_DEFS = Object.freeze([
  {
    key: "agentProfile",
    title: "Agent Profile",
    fields: [
      {
        key: "agent_tenure",
        label: "Est. Time in Industry",
        tooltip:
          "Total time this contact has been transacting or registered with the MLS.",
        kind: KIND.tenure,
      },
      {
        key: "avg_time_at_office",
        label: "Average Time at Office(s)",
        tooltip:
          "Average time (in months) agents typically stay affiliated with the current office.",
        kind: KIND.tenure,
      },
      {
        key: "brand_name",
        label: "Brand name",
        tooltip: "Brand or franchise name associated with the current office.",
        kind: KIND.text,
      },
    ],
  },
  {
    key: "predictions",
    title: "Predictions & Tags",
    fields: [
      {
        key: "future_growth_perc_tag",
        label: "Expected Future Growth",
        tooltip:
          "Predicts whether this contact is on track to grow, hold steady, or slow down in production. Tiers: high, moderate, stable, limited.",
        kind: KIND.text,
      },
      {
        key: "likelihood_to_move_tag",
        label: "Likelihood to Move",
        tooltip:
          "Predicts the chance of moving to a different brokerage. Tiers: high, moderate, low.",
        kind: KIND.text,
      },
    ],
  },
  {
    key: "classification",
    title: "Agent Classification",
    titleTooltipKey: "colorLegend",
    fields: BOOLEAN_SECTION_FIELDS,
  },
  {
    key: "production",
    title: "Production & Activity",
    fields: [
      {
        key: "ltm_est_gci",
        label: "LTM Estimated GCI",
        tooltip:
          "Estimated Gross Commission Income (GCI) in the last twelve months.",
        kind: KIND.currency,
      },
      {
        key: "prev_ltm_sales_volume",
        label: "Previous LTM Sales Volume",
        tooltip:
          "Agent's total sales volume in the twelve months prior to the LTM period.",
        kind: KIND.currency,
      },
      {
        key: "prev_ltm_closed_units",
        label: "Previous LTM Closed Units",
        tooltip:
          "Total closed units in the twelve months prior to the last twelve months. Units take into account cobrokers on the same side of the deal.",
        kind: KIND.decimal,
      },
      {
        key: "pending_listings",
        label: "Pending Listings",
        tooltip:
          "Number of listings currently under contract (pending) held by the agent.",
        kind: KIND.int,
      },
      {
        key: "ltm_rental_count",
        label: "LTM Rental Count",
        tooltip: "Number of rental transactions in the last twelve months.",
        kind: KIND.int,
      },
    ],
  },
  {
    key: "geography",
    title: "Geography",
    fields: [
      {
        key: "most_transacted_city",
        label: "Most Transacted City",
        tooltip:
          "City where the agent conducted the most transactions in the last twelve months (LTM).",
        kind: KIND.text,
      },
      {
        key: "most_transacted_zip",
        label: "Most Transacted Postal Code",
        tooltip:
          "ZIP code where the agent conducted the most transactions in the last twelve months (LTM).",
        kind: KIND.text,
      },
    ],
  },
  {
    key: "metadata",
    title: "Metadata",
    fields: [
      {
        key: "courted_updated_at",
        label: "Last Updated",
        tooltip: "Last update to this contact's record.",
        kind: KIND.date,
      },
    ],
  },
]);

export const computeSectionPresence = (attrs) => {
  if (!attrs) {
    return SECTION_DEFS.reduce(
      (acc, def) => ({ ...acc, [def.key]: false }),
      {},
    );
  }
  return SECTION_DEFS.reduce((acc, def) => {
    acc[def.key] = def.fields.some((field) => attrs[field.key] != null);
    return acc;
  }, {});
};

// Shared PropTypes shape — every Courted attribute the panel renders. Used by
// both index.jsx (lead.courted_attributes) and CourtedSections (attrs prop) so
// the shape stays in one place.
export const COURTED_ATTRIBUTES_SHAPE = PropTypes.shape({
  agent_tenure: PropTypes.number,
  avg_time_at_office: PropTypes.number,
  brand_name: PropTypes.string,
  future_growth_perc_tag: PropTypes.string,
  likelihood_to_move_tag: PropTypes.string,
  ltm_est_gci: PropTypes.number,
  prev_ltm_sales_volume: PropTypes.number,
  prev_ltm_closed_units: PropTypes.number,
  pending_listings: PropTypes.number,
  ltm_rental_count: PropTypes.number,
  most_transacted_city: PropTypes.string,
  most_transacted_zip: PropTypes.string,
  courted_updated_at: PropTypes.string,
  at_gte_one_transaction: PropTypes.bool,
  at_manager_managing_broker: PropTypes.bool,
  at_mostly_buy: PropTypes.bool,
  at_mostly_list: PropTypes.bool,
  at_ready_to_move: PropTypes.bool,
  at_rising_star: PropTypes.bool,
  at_team_leader: PropTypes.bool,
  at_team_member: PropTypes.bool,
  at_trouble_at_office: PropTypes.bool,
});
